import {
  ErrorDetailsDtoErrorCodeEnum,
  RegisterCompanyDataDto,
  ResponseErrorDto,
} from '@interfaces/models';
import { getCompanyData as getCompanyDataApi, getCompanyDetails } from '@api/company';
import { defaultCreateFormValues } from '@pages/cost-centers/utils';
import { CostCenterFormType } from '@pages/cost-centers/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { useCallback, useState } from 'react';
import { toast } from '@ui/components';

export const useCostCenterForm = ({
  form,
  editMode = false,
}: {
  form: CostCenterFormType;
  editMode?: boolean;
}) => {
  const { companyId } = useCurrentUser();
  const { t } = useTranslation();
  const nip = form.watch('nip');

  const [isNipChecked, setIsNipChecked] = useState(!!editMode);

  const isNipValid =
    (!form.formState.errors.nip || form.formState.errors.nip?.type === 'non-blocking') && !!nip;

  const setNipFailedToFetchError = () => {
    form.setError('nip', {
      message: t(`FORMS.NIP_NOT_FOUND`),
      type: 'non-blocking',
    });
  };

  const setCompanyDataToForm = useCallback(
    (details: RegisterCompanyDataDto) => {
      form.setValue('nip', details?.nip ?? defaultCreateFormValues.nip);
      form.setValue('companyName', details?.name ?? defaultCreateFormValues.companyName);
      form.setValue('street', details?.street ?? defaultCreateFormValues.street);
      form.setValue(
        'buildingNumber',
        details?.buildingNumber ?? defaultCreateFormValues.buildingNumber,
      );
      form.setValue(
        'apartmentNumber',
        details?.apartmentNumber ?? defaultCreateFormValues.apartmentNumber,
      );
      form.setValue('zipCode', details?.zipCode ?? defaultCreateFormValues.zipCode);
      form.setValue('city', details?.city ?? defaultCreateFormValues.city);
    },
    [form],
  );

  const { data: companyDetails, isLoading: isCompanyDetailsLoading } = useQuery({
    queryKey: ['companyDetails'],
    queryFn: () => getCompanyDetails(companyId!),
    enabled: !!companyId && !editMode,
  });

  const { mutate: getCompanyDataMutation, isPending: isCompanyDataPending } = useMutation({
    mutationFn: getCompanyDataApi,
    onSuccess: (data) => {
      setCompanyDataToForm(data);
      setIsNipChecked(true);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      if (error.errorCode === ErrorDetailsDtoErrorCodeEnum.REGISTRATION_NIP_NOT_FOUND) {
        setNipFailedToFetchError();
        return;
      }
      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  const getCompanyData = useCallback(() => {
    getCompanyDataMutation(nip);
  }, [getCompanyDataMutation, nip]);

  const copyCompanyDataHandler = useCallback(() => {
    if (!!companyDetails) {
      setCompanyDataToForm({
        name: companyDetails.companyName,
        nip: companyDetails.nip,
        street: companyDetails.primaryAddress.street,
        buildingNumber: companyDetails.primaryAddress.buildingNumber,
        apartmentNumber: companyDetails.primaryAddress.apartmentNumber,
        zipCode: companyDetails.primaryAddress.zipCode,
        city: companyDetails.primaryAddress.city,
      });
    }
  }, [companyDetails, setCompanyDataToForm]);

  return {
    isCompanyDetailsLoading,
    copyCompanyDataHandler,
    isCompanyDataPending,
    getCompanyData,
    isNipChecked,
    isNipValid,
    t,
  };
};
